<template>
  <div class="chartGluDailyTable_ctn" :style="{ zoom: zoomRatio }" :class="{ longLanguage: isLongLanguage }">
    <el-table
      :data="tableData"
      border
      stripe
      :style="table1Style"
      :cell-class-name="tableCellClassName"
      :row-class-name="tableRowClassName">
      <el-table-column
        prop="date"
        label=""
        :width="cellWidth0">
        <template slot-scope="scope">
          <div class="cell_date cell-ellipsis" v-if="!scope.row.isSum">
            {{ scope.row.date | readableWeek }}
          </div>
          <div class="cell_date cell-ellipsis cell_date--isSum" v-else>
            <div>{{ $t(scope.row.date) }}</div>
            <div>({{ $t('By hour') }})</div>
          </div>
          <div class="cell_date cell-ellipsis cell_date--tip" v-if="!scope.row.isSum">
            {{ scope.row.date | formatTime('YYYY/MM/DD') }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-for="v in 24"
        :prop="`cell${v - 1}`"
        :label="`${v - 1}`"
        :key="`dailyTable_column${v}_${id}`"
        :width="cellWidth" >
        <template slot-scope="scope">
          <span class="cell_content--normal" v-if="!!scope.row[`cell${v - 1}`] && !!scope.row[`cell${v - 1}`].isNormal">{{ scope.row[`cell${v - 1}`].normal }}<sup v-show="scope.row[`cell${v - 1}`].normalCount > 1">*</sup></span>
          <cell v-if="!!scope.row[`cell${v - 1}`] && (scope.row[`cell${v - 1}`].min || scope.row[`cell${v - 1}`].max)" :value="scope.row[`cell${v - 1}`].min" :is-complex="scope.row[`cell${v - 1}`].minCount > 1" :data-value="scope['row'][`cell${v - 1}`]['min']" :bg-color="cminC"  /> 
          <cell v-if="!!scope.row[`cell${v - 1}`] && (scope.row[`cell${v - 1}`].min || scope.row[`cell${v - 1}`].max)" :value="scope.row[`cell${v - 1}`].max" :is-complex="scope.row[`cell${v - 1}`].maxCount > 1" :bg-color="cmaxC" />
        </template>
      </el-table-column>
      <el-table-column
        :prop="`cell24`"
        label="Amount(By day)"
        :key="`dailyTable_column25_${id}`"
        :width="cell24Width" >
        <template slot="header">
          <div class="amountByDay">{{ $t('Amount') }}</div>
          <div class="amountByDay">({{ $t('By day') }})</div>
        </template>
        <template slot-scope="scope">
          <cell v-if="!!scope.row[`cell24`]" :value="scope.row[`cell24`].min" :data-value="scope['row'][`cell24`]['min']" :bg-color="cminC" /> 
          <cell v-if="!!scope.row[`cell24`]" :value="scope.row[`cell24`].max" :bg-color="cmaxC" />
        </template>
      </el-table-column>
    </el-table>
    <el-table
      border
      stripe
      :style="table2Style"
      :data="tableData2"
      :cell-class-name="tableCellClassName2"
      :row-class-name="tableRowClassName"
      >
      <el-table-column
        :label="$t('Summary Statistics')"
        :width="cellWidth2"
        :class-name="table2FlexClass"
        >
        <template slot-scope="scope">
          <div class="chartGluDailyTable_table2--columnRow chartGluDailyTable_table2--columnRow1" v-if="scope.row">
            <div class="chartGluDailyTable_table2--columnLeft">
              <div class="chartGluDailyTable_table2--label">{{ $t('Total Insulin')}}</div><div class="value">{{ scope.row.insulin }}</div>
            </div>
            <div class="chartGluDailyTable_table2--columnRight">
              <div class="chartGluDailyTable_table2--label">{{ $t('BG Mean')}}</div><div class="value">{{ scope.row.mean }}</div>
            </div>
          </div>
          <div class="chartGluDailyTable_table2--columnRow chartGluDailyTable_table2--columnRow2" v-if="scope.row">
            <div class="chartGluDailyTable_table2--columnLeft">
              <div class="chartGluDailyTable_table2--label">{{ $t('Basal Dose')}}</div><div class="value">{{ scope.row.basal }}</div>
            </div>
            <div class="chartGluDailyTable_table2--columnRight">
              <div class="chartGluDailyTable_table2--label">{{ $t('Bolus Dose')}}</div><div class="value">{{ scope.row.bolus }}</div>
            </div>
          </div>          
        </template>
      </el-table-column>
    </el-table>
    <div :id="`wtgluDailyTable_l_${id}`" class="legend">
    </div>
  </div>
</template>
<script>
import reDrawTrigger from '@mx/reDrawTrigger'
import { _bgSortOriginDataByDayList } from '@u/reportHelper'
import { Table, TableColumn } from 'element-ui'
import dayjs from 'dayjs'
import * as d3 from 'd3'
import cell from './cell'

import { isHighGlu, isLowGlu, isNormalGlu, findObjIndexInArray } from '@u'

import { derrorFactory, errorFactory, dlogFactory } from '@u/log'
const derror = derrorFactory('ChartGluDailyTable', true)
const error = errorFactory('ChartGluDailyTable', true)
const log = dlogFactory('ChartGluDailyTable', true)

let _id = 1

// const weekdaysShort = dayjs.weekdaysShort()
export default {
  name: 'CChartGluDailyTable',
  mixins: [reDrawTrigger],
  components: {
    elTable: Table,
    elTableColumn: TableColumn,
    cell
  },
  props: {
    sumValue: {
      type: [Object],
      default: () => {
        return {}
      }
    },
    sumRow: {
      type: [Object],
      default: () => {
        return {}
      }
    },
    showSum: {
      type: [Boolean],
      default: false
    },
    values: {
      type: [Array],
      default: () => []
    }
  },
  data() {
    _id++
    const id = `cgdailyt_${+new Date()}_${_id}`
    return {
      id
    }
  },
  mounted() {
    this.rendergluDailyTables(false)
    this.renderLegends(false)
  },
  computed: {
    cellWidth0() {
      const { isLongLanguage } = this
      
      let rst = 100 // english
      if (isLongLanguage) {
        rst = 108
      }
      return rst
    },
    cell24Width() {
      const { cellWidth, isLongLanguage } = this
      if (isLongLanguage) {
        return cellWidth * 2
      } else {
        return cellWidth * 1.6
      }
    },
    cellWidth() {
      const { isLongLanguage } = this
      // let rst = 55 // english
      let rst = 46 // english
      if (isLongLanguage) {
        rst = 41
      }
      derror(`cellWidth: ${rst}`)
      return rst
    },
    cellWidth2() {
      const { isLongLanguage } = this
      
      // let rst = 170 // english
      let rst = 360 // english
      if (isLongLanguage) {
        rst = 544
      }
      derror(`cellWidth2: ${rst}`)
      return rst
    },
    table1Style() {
      const { cellWidth, cellWidth0, cell24Width } = this
      const rst = {
        width: `${cellWidth * 24 + cell24Width + cellWidth0 + 2}px`,
        marginRight: `2px`
      }
      return rst
    },
    table2Style() {
      const { cellWidth2 } = this
      const rst = {
        width: `${cellWidth2 + 2}px`,
        overlow: 'hidden'
      }
      return rst
    },
    tableData() {
      const { values = [], showSum, isUnitMg } = this
      
      // 将每一天的数据归类一下
      // step1, 将所有数据归类到24个小时间隔 0-1, 1-2, ...
      const sortedOriginDataByDayList = _bgSortOriginDataByDayList(values)
      
      // step2, 构造出ui渲染需要的tableData (tdata)
      const tdata = []
      
      sortedOriginDataByDayList.forEach((oneDay, i) => {
        const { list: oneDayPerHourData, date } = oneDay
        
        const row = { date }
        
        let minCount = 0
        let maxCount = 0
        
        oneDayPerHourData.forEach((record, j) => {
          if (!Array.isArray(record)) {
            // 直接认为，没有数据
            row[`cell${j}`] = undefined
          } else {
            // 有数据
            const l = record.length
            const max = record[0] // 最大血糖值的object
            const oneHourMaxCount = record.reduce((prev, cur) => {
              return +prev + (isHighGlu(cur.bloodGlucose) ? 1 : 0)
            }, 0)
            const oneHourMinCount = record.reduce((prev, cur) => {
              return +prev + (isLowGlu(cur.bloodGlucose) ? 1 : 0)
            }, 0)
            const oneHourNormalCount = record.reduce((prev, cur) => {
              return +prev + ((!isLowGlu(cur.bloodGlucose) && !isHighGlu(cur.bloodGlucose)) ? 1 : 0)
            }, 0)
            const min = record[l - 1] // 最小血糖值的object
            
            const hasMinV = isLowGlu(min['bloodGlucose'])
            const hasMaxV = isHighGlu(max['bloodGlucose'])
            
            const _v = {}
            
            if (hasMinV) {
              _v.min = isUnitMg ? (+min.bloodGlucose).toFixed(0) : (+min.bloodGlucose).toFixed(1)
              _v.minCount = oneHourMinCount
            }
            
            if (hasMaxV) {
              _v.max = isUnitMg ? (+max.bloodGlucose).toFixed(0) : (+max.bloodGlucose).toFixed(1)
              _v.maxCount = oneHourMaxCount
            }

            if (!hasMinV && !hasMaxV) {
              // 既没有最大值，也没有最小值。这种情况下分2种: 1, 要么没值;2,要么值都是正常血糖值
              if (l > 0) {
                // 都是正常血糖值
                _v.isNormal = true
                _v.normal = isUnitMg ? (+max.bloodGlucose).toFixed(0) : (+max.bloodGlucose).toFixed(1)
                _v.normalCount = oneHourNormalCount
              }
            }

            /* ++++++++++计算高低血糖值数量++++++++++ */
            record.forEach(one => {
              const isMin = isLowGlu(one['bloodGlucose'])
              const isMax = isHighGlu(one['bloodGlucose'])

              if (isMin) {
                minCount++
              }
              if (isMax) {
                maxCount++
              }
            })
            /* ++++++++++End of 计算高低血糖值数量++++++++++ */
            
            row[`cell${j}`] = _v
          }
        })
        
        row[`cell24`] = {
          min: minCount,
          max: maxCount
        }
        
        row.name = `row${i}`
        
        tdata.push(row) // 插入table的一行
      })
      
      // step3 再插入一行(统计信息)到数据里头(tdata)
      if (showSum) {
        tdata.push(this.sumRow)
      }
      
      return tdata
    },
    tableData2() {
      const { values = [], showSum, sumValue } = this
      
      const rows = values.map((oneDay, i) => {
        const { date, threeChartData = {}} = oneDay
        const { NumberOfBgMeasurements = {}, insulinChart = {}} = threeChartData
        const { bgMean } = NumberOfBgMeasurements
        const { totalBasal, totalBolus, list = [] } = insulinChart
        // const totalInsulin = list.reduce((prev, cur) => {
        //   let rst = prev
          
        //   if (cur && cur.dateTimeStr) {
        //     const t = cur.dateTimeStr
        //     const fmt = 'YYYY-MM-DD'
        //     if (`${date}`.trim() === dayjs(t).format(fmt)) {
        //       const v = +cur.value
        //       rst += v
        //     }
        //   }
          
        //   return rst
        // }, 0)
        const totalInsulin = +totalBasal + +totalBolus

        /* eslint-disable */
        return {
          date,
          insulin: (Number.isNaN(+totalInsulin)) ? '-' : (totalInsulin).toFixed(1),
          bolus: (Number.isNaN(+totalBolus)) ? '-' : (+totalBolus).toFixed(1),
          basal: (Number.isNaN(+totalBasal)) ? '-' : (+totalBasal).toFixed(1),
          mean: bgMean ? bgMean : '-'
        }
        /* eslint-enable */
      })

      if (showSum) {
        // rows.push(undefined) // 推一行进去，占位
        rows.push({
          insulin: (Number.isNaN(+sumValue.insulin)) ? '-' : (+sumValue.insulin).toFixed(1),
          bolus: (Number.isNaN(+sumValue.bolus)) ? '-' : (+sumValue.bolus).toFixed(1),
          basal: (Number.isNaN(+sumValue.basal)) ? '-' : (+sumValue.basal).toFixed(1),
          mean: (sumValue.mean) ? sumValue.mean : '-'
        })
      }
      return rows
    },
    table2FlexClass() {
      const rst = ['chartGluDailyTable_table2--column']

      // lang,语言包特殊化处理
      const { lang } = this
      const leftWider = []
      const rightWider = ['cs', 'es'] // 捷克语，右边宽一点

      if (leftWider.includes(`${lang}`)) {
        rst.push('chartGluDailyTable_table2--columnLeftWider')
      }

      if (rightWider.includes(`${lang}`)) {
        rst.push('chartGluDailyTable_table2--columnRightWider')
      }

      const str = rst.join(' ')
      
      return str
    }
  },
  methods: {
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = 'Amount' // i18n
          return
        }
        
        const values = data.map(item => Number(item[column.property]))
        sums[index] = values.reduce((prev, curr) => {
          const value = Number(curr)
          if (!isNaN(value)) {
            return prev + curr
          } else {
            return prev
          }
        }, 0)
      })
      
      return sums
    },
    tableRowClassName({ row, rowIndex }) {
      const { date } = row || {}

      if (date && (dayjs(date).weekday()) === 6) {
        return 'withSplitter'        
      }
      
      return ''
    },
    tableCellClassName({ row = {}, column = {}}) {
      const { isSum = false } = row
      const { property = '' } = column
      const ps = `${property}`
      if (isSum && ps === 'cell24') {
        return 'corner_cell--bright'
      }
      if (ps === 'cell23') {
        return 'cell_padRight'
      }
      return ''
    },
    tableCellClassName2({ row = {}, column = {}}) {
      // const { isSum = false } = row
      // const { property = '' } = column
      // const ps = `${property}`

      // console.log(`${JSON.stringify(row)}`)
      // console.log(`${JSON.stringify(column)}`)

      if (row.insulin === undefined && row.bolus === undefined && row.mean === undefined) {
        return 'corner_cell--bottom'
      }

      return ''
    },
    refreshTriggered() {
      console.error(`ChargluDailyTable ${this.id}, this.refreshTriggered`)
      this.rendergluDailyTables(true)
      this.renderLegends(true)
    },
    renderLegends(isUpdate = false) {
      const { id, cmaxC, cminC, isLongLanguage } = this
      // console.error(`id: ${id}`)
      const legendId = `wtgluDailyTable_l_${id}`
      const $legend = d3.select(`#${legendId}`)
      const svgId = `wtgluDailyTable_l_svg_${id}`
      
      const svg = isUpdate ? $legend.select(`#${svgId}`) : $legend.append('svg')
      
      svg.attr('width', 500)
        .attr('height', 40)
      
      if (!isUpdate) {
        svg.attr('id', svgId)
      }
      
      const gId = `wtgluDailyTable_l_svg_g_${id}`
      const chartG = isUpdate ? svg.select(`#${gId}`) : svg.append('g')
      if (!isUpdate) { // 第一次(init)
        chartG.attr('id', gId)
      }
      
      const textW = 10
      const paddingLeft = 10 * textW
      const nextPaddingLeft = (isLongLanguage ? 9 : 7) * textW
      const nextPaddingLeftText = nextPaddingLeft + 3 * textW
      const marginTop = textW
      const y = 10

      chartG.selectAll('path')
        .data([
          {
            color: cmaxC,
            x: paddingLeft,
            y: y + marginTop
          },
          {
            color: cminC,
            y: y + marginTop,
            x: paddingLeft + nextPaddingLeft,
          }
        ])
        .join('path')
        .attr('d', d => {
          let rst = ''
          const h = textW
          const w = h * 2
          const { x, y } = d
          rst = `M${x} ${Math.round(y - h / 2)}l${w} 0l0 ${h}l-${w} 0Z`
          return rst
        })
        .attr('fill', d => d.color)
      
      chartG.selectAll('text')
        .data([
          {
            text: this.$t('High'),
            color: '#333',
            x: paddingLeft + 3 * textW,
            y: y + (textW / 2.5) + marginTop
          },
          {
            text: this.$t('Low'),
            color: '#333',
            y: y + (textW / 2.5) + marginTop,
            x: paddingLeft + nextPaddingLeftText
          }
        ])
        .join('text')
        .text(d => d.text)
        .attr('font-size', d => `${d.fs ? d.fs : 10}`)
      // .attr('style', d => `text-anchor: ${d.anchor ? d.anchor : 'end'}`)
        .attr('x', d => {
          return d && d.marginLeft ? (d.x - 0 + 10) : d.x
        })
        .attr('y', d => d.y)
        .attr('fill', d => d.color)
    },
    rendergluDailyTables(isUpdate = false) {
    }
  }
}
</script>
<style lang="scss" scoped>
$br: 0.8em;  
$br--px: 8px;
$fs--px: 14px;
$fs-small--px: 12px;
$fs-large--px: 16px;
$c: #454545;
@import '@/styles/mixin';
@import '@/styles/variables';
::v-deep .el-table {    
    color: $c;
    .cell-ellipsis {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
    &.el-table--border {
        border-radius: $br--px;
        border-radius: $br;
    }
    .cell {
        padding: 0 0;
        padding-left: 0;
        line-height: 18px;
        font-size: 16px;        
    }
    .el-table__cell {
        padding: 0 0;
    }
    tr.el-table__row > .el-table__cell {
        position: relative;
        > .cell {
            height: 100%;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            > span {
                &.cell_content--normal {
                    text-align: center;
                }
            }
            > div {
                flex-grow: 1;
            }            
        }

        &.chartGluDailyTable_table2--columnLeftWider > .cell {
            .chartGluDailyTable_table2--columnLeft {
                width: 60%;
            }
            .chartGluDailyTable_table2--columnRight {
                width: 40%;
            }
        }

        &.chartGluDailyTable_table2--columnRightWider > .cell {
            .chartGluDailyTable_table2--columnLeft {
                width: 40%;
            }
            .chartGluDailyTable_table2--columnRight {
                width: 60%;
            }
        }
    }
}
::v-deep .el-table--border .el-table__cell:first-child .cell {
    padding-left: 0;
}
::v-deep .withSplitter {
    > td {
        border-bottom: 2px solid black;
    }
}
.cell_date {
    color: black;
    text-align: right;
    padding-right: 0.5em;
    font-size: $fs-small--px;
    &.cell_date--tip {
        font-size: $fs-small--px;
        /* opacity: 0.6; */
    }
    &.cell_date--isSum {
        @include flexColumn(space-around, flex-end);
    }
}
::v-deep .el-table__header-wrapper {
    > table {
        height: 40px;
        background-color: #FAFAFA;
    }
    thead > tr > th {
        overflow: visible;
        border-right: 0;
        background-color: #D8D8D8;
        height: 48px;
        > .cell {
            left: -1.4em;
            left: -0.6em;
            padding-left: 0;
            padding-right: 0;
            font-size: $fs--px;
        }
        &:nth-last-child(2) {
            > .cell {
                left: 0;
                overflow: visible;
                white-space: nowrap;
                direction: rtl;
                text-indent: 2px;
            }
        }
    }
}
::v-deep .el-table__footer-wrapper {
    .el-table__footer {
        height: 36px;
        tbody > tr > td.el-table__cell {
            text-align: center;
        }
    }    
}
::v-deep .el-table--striped .el-table__body {
    tr.el-table__row td.el-table__cell {
        background: #FAFAFA;
    }
    
    tr.el-table__row--striped td.el-table__cell {
        background: white;
    }    
}
.chartGluDailyTable_ctn {
    ::v-deep {
        box-sizing: border-box;
        overflow: hidden;
        .corner_cell--bright {
            border-bottom-right-radius: $br--px;
            border-bottom-right-radius: $br;
        }
        .corner_cell--bottom {
            border-bottom-left-radius: $br--px;
            border-bottom-left-radius: $br;
            border-bottom-right-radius: $br--px;
            border-bottom-right-radius: $br;
        }
        .cell_padRight {
            border-right: 10px solid #d8d8d8;
        }

        th.chartGluDailyTable_table2--column {
            text-align: center;
        }
        td.chartGluDailyTable_table2--column {
            text-align: left;
            .chartGluDailyTable_table2--columnRow {
                color: black;
                box-sizing: border-box;
                /* padding: 2px 11px; */
                font-size: 8px;
                height: 15px;
                overflow: hidden;
                @include flexRow(space-between, center);
                > div {
                    width: 50%;
                    flex-grow: 1;
                    @include flexRow(flex-start, center);
                    .chartGluDailyTable_table2--label, .value {
                        font-size: $fs-large--px;
                    }
                    .value {
                        font-size: $fs-large--px * 0.9;
                        flex-grow: 0;
                        flex-shrink: 0;
                    }
                    &:first-child {
                    }
                    > div {
                        &:last-child {
                            @include bBox;
                            /* padding-right: 4px; */
                            /* padding-right: 2em; */
                            padding-left: 5px;
                            padding-left: .5em;
                        }
                    }
                }
            }
        }
        > div.el-table {
            display: inline-block;
            tr.el-table__row {
                height: 46px;
            }
        }
    }
}
.amountByDay {
    font-size: 0.9em;
}
.longLanguage .amountByDay {
    font-size: 0.75em;
}
</style>
